import React from 'react'

import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { breakpoints, RadioInputLabel, spacing } from '@cash-design-system/react'
import { FormBlockerElementEmojiPickerElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { ArcadeRadioGroupOption, RadioGroup, RadioGroupOption } from '@cash-web/shared-feature-form'
import { unicodeToEmojiSvg } from '@cash-web/shared-ui-emojis'

import { FormBlockerAdditionalElementProps } from '../FormBlockerElement/FormBlockerElement'

const EmojiRadioGroupContainer = styled.div`
  padding: 0 1rem;
`

const InputWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  row-gap: ${spacing.l};
  place-items: center;
  ${breakpoints.tablet} {
    column-gap: 2.75rem;
  }
`

const EmojiRadioInputLabel = styled(RadioInputLabel)`
  border-radius: 50%;
  height: 4.5rem;
  width: 4.5rem;
  padding: 0;

  ${props =>
    props.checked &&
    css`
      background-color: ${props.theme.colors.formFieldBackground};
      border: ${props.theme.pxToRem(3)} solid ${props.theme.colors.primary};
    `}
  ${breakpoints.tablet} {
    height: 5.5rem;
    width: 5.5rem;
  }
`

const EmojiImage = styled.img`
  height: 3rem;
  margin: auto;
  width: 3rem;
  ${breakpoints.tablet} {
    height: 3.5rem;
    width: 3.5rem;
  }
`

const getEmoji = ({ unicode, darkMode }: { unicode?: string; darkMode: boolean }): string | undefined => {
  if (unicode == null) {
    throw Error(`EmojiPickerElement: Emoji "${unicode}" not found`)
  }

  return unicodeToEmojiSvg({ unicode, size: 48, darkMode })
}

export function EmojiPickerElement({
  categories,
  initial_selection,

  // additional form blocker props
  formSectionIndex,
}: FormBlockerElementEmojiPickerElement & FormBlockerAdditionalElementProps): React.ReactElement {
  const { darkMode } = useTheme()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const emoji = categories
    ?.flatMap(category =>
      category.emojiOptions?.map(emojiOption => {
        const emoji = emojiOption ? getEmoji({ unicode: emojiOption.unicodeEmoji, darkMode }) : undefined
        if (isArcadeMigrationEnabled) {
          return emoji
            ? {
                label: emojiOption.accessibilityText,
                value: emojiOption?.unicodeEmoji,
                cellProps: {
                  body: <EmojiImage alt={emojiOption.accessibilityText} src={emoji} />,
                },
              }
            : undefined
        }
        return emoji
          ? {
              label: <EmojiImage alt={emojiOption.accessibilityText} src={emoji} />,
              value: emojiOption?.unicodeEmoji,
            }
          : null
      })
    )
    .filter(value => value != null)

  const initialCategoryIndex = initial_selection?.categories_index || 0
  const initialOptionsIndex = initial_selection?.options_index || 0
  const defaultEmoji = categories?.[initialCategoryIndex]?.emojiOptions?.[initialOptionsIndex]?.unicodeEmoji

  return (
    <EmojiRadioGroupContainer>
      <RadioGroup
        defaultValue={defaultEmoji}
        InputWrapper={InputWrapper}
        name={`${formSectionIndex}.emoji_picker_result.selection`}
        options={isArcadeMigrationEnabled ? (emoji as RadioGroupOption[]) : (emoji as ArcadeRadioGroupOption[])}
        StyledLabelComponent={isArcadeMigrationEnabled ? undefined : EmojiRadioInputLabel}
        inputStyle={isArcadeMigrationEnabled ? 'image' : 'cell'}
      />
    </EmojiRadioGroupContainer>
  )
}
